import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

export interface FilterOption {
  value: string;
  label: string;
  color?: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
  selectedValue: string;
  placeholder?: string;
}

export interface SearchFilterConfig {
  searchPlaceholder: string;
  showSearch: boolean;
  showFilter: boolean;
  filterOptions: FilterOption[]; // Legacy single filter support
  filterGroups?: FilterGroup[]; // New multiple filter support
  resultLabel: string; // e.g., "users", "items", "records"
  enableMultipleFilters?: boolean;
  // Set false to hide the "X of Y" count. Useful with server-side paging, where the toolbar
  // only knows the loaded page and "X of Y" would be misleading. Defaults to showing it.
  showResultsCount?: boolean;
}

export interface MultipleFiltersState {
  [filterKey: string]: string;
}

@Component({
  selector: 'app-search-filter-toolbar',
  templateUrl: './search-filter-toolbar.component.html',
  styleUrls: ['./search-filter-toolbar.component.scss']
})
export class SearchFilterToolbarComponent implements OnInit {
  @Input() config: SearchFilterConfig = {
    searchPlaceholder: 'Search...',
    showSearch: true,
    showFilter: true,
    filterOptions: [],
    resultLabel: 'items',
    enableMultipleFilters: false
  };
  
  @Input() searchText: string = '';
  @Input() selectedFilter: string = 'all'; // Legacy single filter
  @Input() selectedFilters: MultipleFiltersState = {}; // New multiple filters
  @Input() totalCount: number = 0;
  @Input() filteredCount: number = 0;
  @Input() hasPagination: boolean = false;
  @Input() isMobileDevice: boolean = false;
  @Input() isFilterExpanded: boolean = false;
  /**
   * Whether the chevron that expands the toolbar on a phone is offered. A screen
   * with nothing behind it turns this off; its sections then show as they do on
   * a desktop rather than hiding behind a control that reveals nothing.
   */
  @Input() showMobileExpand: boolean = true;
  @Input() hasActiveUnsavedFilters: boolean = false;
  /** Reserves room at the right of the search field for a projected
   *  [searchAction] control, so the text never runs under it. */
  @Input() hasSearchAction: boolean = false;
  /** A host is projecting into [toolbarAction] — the slot opposite the search field. Declared
   *  rather than detected so the empty flex item never takes a gap of its own. */
  @Input() hasToolbarAction: boolean = false;
  
  @Output() searchChanged = new EventEmitter<string>();
  @Output() filterChanged = new EventEmitter<string>(); // Legacy single filter
  @Output() filtersChanged = new EventEmitter<MultipleFiltersState>(); // New multiple filters
  @Output() searchCleared = new EventEmitter<void>();
  @Output() toggleExpandFiltersEvt = new EventEmitter<boolean>();

  isFilterDropdownOpen = false;
  openFilterDropdowns: { [key: string]: boolean } = {};

  ngOnInit() {
    // Initialize multiple filters if enabled
    if (this.config.enableMultipleFilters && this.config.filterGroups) {
      this.config.filterGroups.forEach(group => {
        if (!this.selectedFilters[group.key]) {
          this.selectedFilters[group.key] = group.options[0]?.value || 'all';
        }
        this.openFilterDropdowns[group.key] = false;
      });
    }

    // Legacy single filter support
    // if (!this.config.enableMultipleFilters) {
    //   // Ensure 'all' option exists
    //   if (this.config.filterOptions.length > 0 && !this.config.filterOptions.find(opt => opt.value === 'all')) {
    //     this.config.filterOptions.unshift({ value: 'all', label: 'All' });
    //   }
    // }
  }

  onSearchInput(event: any) {
    const value = event.target.value ?? '';
    this.searchText = value;
    this.searchChanged.emit(value);
  }

  clearSearch() {
    this.searchText = '';
    this.searchChanged.emit("");
  }

  // Legacy single filter methods
  toggleFilterDropdown() {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  selectFilter(filterValue: string) {
    this.selectedFilter = filterValue;
    this.isFilterDropdownOpen = false;
    this.filterChanged.emit(filterValue);
  }

  getSelectedFilterLabel(): string {
    const selectedOption = this.config.filterOptions.find(opt => opt.value === this.selectedFilter);
    return selectedOption ? selectedOption.label : 'All';
  }

  getSelectedFilterColor(): string {
    const selectedOption = this.config.filterOptions.find(opt => opt.value === this.selectedFilter);
    return selectedOption?.color || '#666';
  }

  // New multiple filter methods
  toggleMultipleFilterDropdown(filterKey: string) {
    this.openFilterDropdowns[filterKey] = !this.openFilterDropdowns[filterKey];
    
    // Close other dropdowns
    Object.keys(this.openFilterDropdowns).forEach(key => {
      if (key !== filterKey) {
        this.openFilterDropdowns[key] = false;
      }
    });
  }

  selectMultipleFilter(filterKey: string, filterValue: string) {
    this.selectedFilters[filterKey] = filterValue;
    this.openFilterDropdowns[filterKey] = false;
    this.filtersChanged.emit({ ...this.selectedFilters });
  }

  getSelectedFilterLabelByKey(filterKey: string): string {
    const filterGroup = this.config.filterGroups?.find(group => group.key === filterKey);
    if (!filterGroup) return 'All';
    
    const selectedValue = this.selectedFilters[filterKey];
    const selectedOption = filterGroup.options.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : filterGroup.placeholder || 'All';
  }

  getSelectedFilterColorByKey(filterKey: string): string {
    const filterGroup = this.config.filterGroups?.find(group => group.key === filterKey);
    if (!filterGroup) return '#666';
    
    const selectedValue = this.selectedFilters[filterKey];
    const selectedOption = filterGroup.options.find(opt => opt.value === selectedValue);
    return selectedOption?.color || '#666';
  }

  isFiltered(): boolean {
    if (this.config.enableMultipleFilters) {
      // Check if any filter is not 'all' or if search text exists
      const hasActiveFilters = Object.values(this.selectedFilters).some(value => value !== 'all');
      return this.searchText.trim() !== '' || hasActiveFilters;
    } else {
      // Legacy single filter logic
      return this.searchText.trim() !== '' || this.selectedFilter !== 'all';
    }
  }

  getActiveFiltersCount(): number {
    if (!this.config.enableMultipleFilters) return 0;
    
    let count = 0;
    if (this.searchText.trim() !== '') count++;
    
    Object.values(this.selectedFilters).forEach(value => {
      if (value !== 'all') count++;
    });
    
    return count;
  }

  clearAllFilters() {
    this.searchText = '';
    if (this.config.enableMultipleFilters && this.config.filterGroups) {
      this.config.filterGroups.forEach(group => {
        this.selectedFilters[group.key] = 'all';
      });
      this.filtersChanged.emit({ ...this.selectedFilters });
    } else {
      this.selectedFilter = 'all';
      this.filterChanged.emit('all');
    }
    this.searchCleared.emit();
  }

  // Helper methods for template bindings
  hasOpenDropdowns(): boolean {
    return this.isFilterDropdownOpen || (this.openFilterDropdowns && Object.values(this.openFilterDropdowns).some(open => open));
  }

  closeAllDropdowns(): void {
    this.isFilterDropdownOpen = false;
    if (this.openFilterDropdowns) {
      Object.keys(this.openFilterDropdowns).forEach(key => {
        this.openFilterDropdowns[key] = false;
      });
    }
  }

  toggleExpandFilters() {
    this.isFilterExpanded = !this.isFilterExpanded;
    this.toggleExpandFiltersEvt.emit(this.isFilterExpanded);
  }

  /** Sections that a phone hides behind the chevron — shown outright when there
   *  is no chevron to reveal them. */
  get showsCollapsibleSections(): boolean {
    return !this.isMobileDevice || this.isFilterExpanded || !this.showMobileExpand;
  }
}
